import { useState } from 'react';
import { X } from 'lucide-react';
import { useWorkoutStore } from '../stores/workoutStore';
import type { Exercise } from '../types/workout';
import { exerciseSchema } from '../schemas/exerciseSchema';
import { formatWorkoutDate } from '../lib/utils';

interface AddExerciseDialogProps {
  date: string;
  sessionIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

export function AddExerciseDialog({
  date,
  sessionIndex,
  isOpen,
  onClose,
}: AddExerciseDialogProps) {
  const { addExercise } = useWorkoutStore();

  const [formData, setFormData] = useState<Exercise>({
    exercise: '',
    sets: 3,
    reps: '10',
    weight: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const handleChange = (field: keyof Exercise, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = exerciseSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    await addExercise(date, sessionIndex, formData);

    setFormData({
      exercise: '',
      sets: 3,
      reps: '10',
      weight: 0,
    });
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setFormData({
      exercise: '',
      sets: 3,
      reps: '10',
      weight: 0,
    });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Add Exercise to {formatWorkoutDate(date)}</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label
              htmlFor="exercise-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Exercise Name *
            </label>
            <input
              id="exercise-name"
              type="text"
              value={formData.exercise}
              onChange={(e) => handleChange('exercise', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="e.g., Barbell Bench Press"
            />
            {errors.exercise && (
              <p className="mt-1 text-sm text-red-600">{errors.exercise}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="exercise-sets"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Sets *
            </label>
            <input
              id="exercise-sets"
              type="number"
              value={formData.sets}
              onChange={(e) =>
                handleChange('sets', parseInt(e.target.value) || 0)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              min="1"
            />
            {errors.sets && (
              <p className="mt-1 text-sm text-red-600">{errors.sets}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="exercise-reps"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reps *
            </label>
            <input
              id="exercise-reps"
              type="text"
              value={formData.reps}
              onChange={(e) => handleChange('reps', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="e.g. 8-10, 30s"
            />
            {errors.reps && (
              <p className="mt-1 text-sm text-red-600">{errors.reps}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="exercise-weight"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Weight (lbs)
            </label>
            <input
              id="exercise-weight"
              type="number"
              value={formData.weight}
              onChange={(e) =>
                handleChange('weight', parseFloat(e.target.value) || 0)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              min="0"
              step="2.5"
            />
            {errors.weight && (
              <p className="mt-1 text-sm text-red-600">{errors.weight}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Use 0 for bodyweight exercises
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Add Exercise
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
